import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import './BreakthroughAssessment.css';

const ASSESSMENT_TYPE = 1;
const TOTAL_QUESTIONS = 14;

const INTRO_PARAS = [
    'In the Design My Life Blueprint, we learned a lot about you — which areas of life are strong, and which could benefit from more balance.',
    'As you enter your Breakthrough Experience, these 14 questions help us personalize the content you need to move past what has gotten in the way of your goals.',
];

function RailCheckIcon() {
    return (
        <svg
            className="dv2-ba__rail-check"
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
        >
            <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * V2 Assessment One for My Breakthrough customization.
 * Reuses the same APIs as classic /assessment?AssessmentType=1 — does not modify V1.
 */
const BreakthroughAssessment = ({ onBack, onComplete }) => {
    const { checkUserSession, changeLoading } = useContext(AppContext);
    const { token, userId } = checkUserSession() || {};

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [index, setIndex] = useState(0);
    const [answers, setAnswers] = useState([]); // { questionId, OptionId }
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!token) return;
            setLoading(true);
            setErrorMsg('');
            const { error, data } = await requestAxios({
                url: `ELearning/GetAssessmentQuestions/${ASSESSMENT_TYPE}`,
                token,
            });
            if (cancelled) return;
            if (error || !data) {
                setErrorMsg('Could not load assessment questions.');
                setQuestions([]);
            } else {
                setQuestions(Array.isArray(data.questions) ? data.questions : []);
            }
            setLoading(false);
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const answeredIds = useMemo(
        () => new Set(answers.map((a) => a.questionId)),
        [answers]
    );

    const current = questions[index] || null;
    const currentAnswer = current
        ? answers.find((a) => a.questionId === current.id)
        : null;
    const allAnswered =
        questions.length > 0 &&
        answers.length >= questions.length &&
        questions.every((q) => answeredIds.has(q.id));
    const isLast = index >= Math.max(0, questions.length - 1);

    const canOpenQuestion = useCallback(
        (i) => {
            if (i <= 0) return true;
            if (i > answers.length) return false;
            return true;
        },
        [answers.length]
    );

    const selectOption = (questionId, optionId) => {
        if (submitting) return;

        setAnswers((prev) => {
            const next = [...prev];
            const existing = next.find((x) => x.questionId === questionId);
            if (existing) {
                existing.OptionId = optionId;
            } else {
                next.push({ questionId, OptionId: optionId });
            }
            return next;
        });
    };

    const goNext = () => {
        if (!currentAnswer || isLast) return;
        setIndex((prev) => Math.min(prev + 1, questions.length - 1));
    };

    const goPrev = () => {
        setIndex((prev) => Math.max(0, prev - 1));
    };

    const completeAssessment = async () => {
        if (!token || !userId || !allAnswered || submitting) return;
        setSubmitting(true);
        setErrorMsg('');
        if (typeof changeLoading === 'function') changeLoading(true);

        const postAssessment = await requestAxios({
            url: 'ELearning',
            method: 'post',
            token,
            data: {
                userId,
                AssessmentType: ASSESSMENT_TYPE,
                AssesmentDetails: answers,
            },
        });

        if (postAssessment.error) {
            setErrorMsg('Could not save your assessment. Please try again.');
            setSubmitting(false);
            if (typeof changeLoading === 'function') changeLoading(false);
            return;
        }

        const createModules = await requestAxios({
            url: 'ELearning/CreateELearningModules',
            method: 'post',
            token,
            data: {
                userId,
                AssessmentType: ASSESSMENT_TYPE,
            },
        });

        if (typeof changeLoading === 'function') changeLoading(false);
        setSubmitting(false);

        if (createModules.error) {
            setErrorMsg(
                'Assessment saved, but modules could not be created. Please refresh and try again.'
            );
            return;
        }

        if (typeof onComplete === 'function') onComplete();
    };

    const totalQs = questions.length || TOTAL_QUESTIONS;
    const answeredCount = answers.length;
    const progressPct = Math.min(
        100,
        Math.round((answeredCount / Math.max(1, totalQs)) * 100)
    );

    return (
        <div className="dv2-ba">
            <div className="dv2-ba__top">
                <button
                    type="button"
                    className="dv2-ba__back"
                    onClick={onBack}
                    disabled={submitting}
                >
                    ← Back to My Breakthrough
                </button>
            </div>

            <div className="dv2-ba__hero">
                <header className="dv2-ba__header">
                    <h1 className="dv2-ba__title">Assessment One</h1>
                    <div className="dv2-ba__intro">
                        {INTRO_PARAS.map((para) => (
                            <p key={para.slice(0, 24)}>{para}</p>
                        ))}
                    </div>
                </header>

                <aside className="dv2-ba__context" aria-label="Assessment progress">
                    <p className="dv2-ba__context-eyebrow">Assessment progress</p>
                    <p className="dv2-ba__context-stat">
                        Question {Math.min(index + 1, totalQs)} of {totalQs}
                    </p>
                    <div className="dv2-ba__context-meter" aria-hidden>
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                    <p className="dv2-ba__context-pct">
                        {answeredCount === 0
                            ? 'Not started'
                            : `${progressPct}% complete`}
                    </p>
                    <p className="dv2-ba__context-note">
                        Personalizing your Breakthrough path
                    </p>
                </aside>
            </div>

            {loading ? (
                <p className="dv2-ba__status">Loading questions…</p>
            ) : null}

            {errorMsg ? <p className="dv2-ba__error">{errorMsg}</p> : null}

            {!loading && questions.length > 0 ? (
                <div className="dv2-ba__body">
                    <nav className="dv2-ba__rail" aria-label="Questions">
                        <div className="dv2-ba__rail-line" aria-hidden />
                        <ol className="dv2-ba__rail-list">
                            {questions.map((q, i) => {
                                const label = `Q ${String(i + 1).padStart(
                                    2,
                                    '0'
                                )}`;
                                const isActive = i === index;
                                const isDone = answeredIds.has(q.id);
                                const open = canOpenQuestion(i);
                                return (
                                    <li key={q.id || i}>
                                        <button
                                            type="button"
                                            className={`dv2-ba__rail-item${
                                                isActive ? ' is-active' : ''
                                            }${isDone ? ' is-done' : ''}`}
                                            disabled={!open || submitting}
                                            onClick={() =>
                                                open && setIndex(i)
                                            }
                                            aria-current={
                                                isActive ? 'step' : undefined
                                            }
                                            aria-label={`${label}${
                                                isDone ? ', completed' : ''
                                            }${isActive ? ', current' : ''}`}
                                        >
                                            <span
                                                className="dv2-ba__rail-dot"
                                                aria-hidden
                                            >
                                                {isDone && !isActive ? (
                                                    <RailCheckIcon />
                                                ) : null}
                                            </span>
                                            <span className="dv2-ba__rail-label">
                                                {label}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>

                    <div className="dv2-ba__panel">
                        <div className="dv2-ba__panel-main">
                            <p className="dv2-ba__progress">
                                Question {index + 1} of {totalQs}
                            </p>
                            <h2
                                className="dv2-ba__question"
                                id="dv2-ba-question"
                            >
                                {current?.text || ''}
                            </h2>

                            <div
                                className="dv2-ba__options"
                                role="radiogroup"
                                aria-labelledby="dv2-ba-question"
                            >
                                {(current?.options || []).map((opt) => {
                                    const selected =
                                        currentAnswer?.OptionId === opt.id;
                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected}
                                            className={`dv2-ba__option${
                                                selected ? ' is-selected' : ''
                                            }`}
                                            disabled={submitting}
                                            onClick={() =>
                                                selectOption(
                                                    current.id,
                                                    opt.id
                                                )
                                            }
                                        >
                                            <span
                                                className="dv2-ba__option-radio"
                                                aria-hidden
                                            />
                                            <span className="dv2-ba__option-text">
                                                {opt.text}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="dv2-ba__nav">
                                {index > 0 ? (
                                    <button
                                        type="button"
                                        className="dv2-ba__nav-btn"
                                        onClick={goPrev}
                                        disabled={submitting}
                                    >
                                        ← Previous
                                    </button>
                                ) : (
                                    <span />
                                )}

                                {isLast && allAnswered ? (
                                    <button
                                        type="button"
                                        className="dv2-ba__complete"
                                        onClick={completeAssessment}
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Saving…' : 'Complete'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className={`dv2-ba__next${
                                            currentAnswer ? ' is-ready' : ''
                                        }`}
                                        onClick={goNext}
                                        disabled={
                                            !currentAnswer || submitting
                                        }
                                    >
                                        Next →
                                    </button>
                                )}
                            </div>
                        </div>

                        <aside className="dv2-ba__insight">
                            <p className="dv2-ba__insight-eyebrow">Tip</p>
                            <p className="dv2-ba__insight-body">
                                Choose the answer that feels most true today.
                                There are no right or wrong answers.
                            </p>
                            <div className="dv2-ba__insight-divider" aria-hidden />
                            <p className="dv2-ba__insight-eyebrow">
                                Why this matters
                            </p>
                            <p className="dv2-ba__insight-body">
                                Your answers help personalize the modules
                                you&apos;ll unlock during Breakthrough.
                            </p>
                        </aside>
                    </div>
                </div>
            ) : null}

            {!loading && !questions.length && !errorMsg ? (
                <p className="dv2-ba__status">No questions available.</p>
            ) : null}
        </div>
    );
};

export default BreakthroughAssessment;
